import React, {Component} from 'react';
import {connect} from 'react-redux';
import RecommendedWrfDialog from '../components/RecommendedWrfDialog';
import VariablesForm from '../components/VariablesForm';
import {DATA_SETS} from '../Constants';
import {
  SUMMER_AFTERNOON_FMC_ENUM,
  SUMMER_AFTERNOON_FMC_USER_CONFIGURABLE
} from '../utils/behaviour_models/shared/ffm';
import './Forms.css';

const HAZARD_ENUM = {
  'Nil (0)': 0,
  'Low (1)': 1,
  'Moderate (2)': 2,
  'High (3)': 3,
  'Very High (3.5)': 3.5,
  'Extreme (4)': 4
};

const FMC_NOTE_COMMON = '1Only affects 1 Oct–31 Mar, 12:00–17:59.';
const FMC_NOTE_WET_ONLY = ' On Mk2 Wet, behaviour outputs are unchanged — only Current FMC changes.';

const SHARED_META = [
  { key: 'slope', displayName: 'Slope (deg)', min: -90, max: 90, row: 1, col: 1, order: 1 },
  {
    key: 'surface_hazard', displayName: 'Surface Fuel Hazard', row: 2, col: 1, order: 2,
    enum: HAZARD_ENUM
  },
  {
    key: 'near_surface_hazard', displayName: 'Near-Surface Hazard', row: 3, col: 1, order: 3,
    enum: HAZARD_ENUM
  },
  { key: 'near_surface_height', displayName: 'Near-Surface Fuel Height (cm)', min: 0, max: 500, row: 3, col: 2, order: 6 },
  {
    key: 'elevated_hazard', displayName: 'Elevated Fuel Hazard', row: 4, col: 1, order: 4,
    enum: HAZARD_ENUM
  },
  { key: 'elevated_height', displayName: 'Elevated Fuel Height (m)', min: 0, max: 99, row: 4, col: 2, order: 7 },
  {
    key: 'bark_hazard', displayName: 'Bark Hazard', row: 5, col: 1, order: 5,
    enum: HAZARD_ENUM
  },
  { key: 'wind_reduction_factor', displayName: 'Wind Reduction Factor', min: 1, max: 20, row: 5, col: 2, order: 8 },
  {
    key: 'calculated_fuel_load', displayName: 'Calculated Fuel Load', row: 6, col: 1, calculated: true
  },
  {
    key: 'lag_temp_rh', displayName: 'Lag Temp & RH', row: 6, col: 2, order: 11,
    enum: { 'Off': 0, '2 Hrs': 2 }
  }
];

const SUMMER_AFTERNOON_FMC_META = {
  key: 'summer_afternoon_fmc',
  displayName: 'Summer afternoon FMC',
  row: 7,
  col: 1,
  order: 12,
  enum: SUMMER_AFTERNOON_FMC_ENUM
};

const WET_ONLY_META = [
  {
    key: 'aspect', displayName: 'Aspect of Slope (deg TN)', editDisplayName: 'Aspect of Slope (deg TN)',
    min: 0, max: 360, row: 1, col: 2, order: 9
  },
  {
    key: 'slope_steepness', displayName: 'Slope Steepness (deg)', min: -90, max: 90, row: 2, col: 2, order: 10
  }
];

// Summer afternoon FMC meta omitted while SUMMER_AFTERNOON_FMC_USER_CONFIGURABLE is false.
const SUMMER_AFTERNOON_FMC_META_BY_TAB = SUMMER_AFTERNOON_FMC_USER_CONFIGURABLE ? {
  [DATA_SETS.VESTA_MK2_DRY]: [{ ...SUMMER_AFTERNOON_FMC_META, note: FMC_NOTE_COMMON }],
  [DATA_SETS.VESTA_MK2_WET]: [{ ...SUMMER_AFTERNOON_FMC_META, note: FMC_NOTE_COMMON + FMC_NOTE_WET_ONLY }]
} : {
  [DATA_SETS.VESTA_MK2_DRY]: [],
  [DATA_SETS.VESTA_MK2_WET]: []
};

const META_BY_DATA_SET = {
  [DATA_SETS.VESTA_MK2_DRY]: [
    ...SHARED_META,
    ...SUMMER_AFTERNOON_FMC_META_BY_TAB[DATA_SETS.VESTA_MK2_DRY]
  ],
  [DATA_SETS.VESTA_MK2_WET]: [
    ...SHARED_META,
    ...WET_ONLY_META,
    ...SUMMER_AFTERNOON_FMC_META_BY_TAB[DATA_SETS.VESTA_MK2_WET]
  ]
};

const SLICE_BY_DATA_SET = {
  [DATA_SETS.VESTA_MK2_DRY]: 'point_data_vesta_mk2_dry',
  [DATA_SETS.VESTA_MK2_WET]: 'point_data_vesta_mk2_wet'
};

class VestaMk2Variables extends Component {
  constructor(props) {
    super(props);
    this.state = { wrfDialogOpen: false };
    this.openWrfDialog = this.openWrfDialog.bind(this);
    this.closeWrfDialog = this.closeWrfDialog.bind(this);
  }

  openWrfDialog(event) {
    event.preventDefault();
    this.setState({ wrfDialogOpen: true });
  }

  closeWrfDialog() {
    this.setState({ wrfDialogOpen: false });
  }

  render() {
    const { data_set, variables } = this.props;
    const panelActions = (
      <div className="c-button c-form__edit-button" onClick={this.openWrfDialog} title="Calculate recommended WRF from overstorey structure">
        <i className="fa fa-calculator"/><label>Calc WRF</label>
      </div>
    );

    return <div className="c-form__vesta-mk2-variables">
      <VariablesForm data_set={data_set}
                     meta_data={META_BY_DATA_SET[data_set]}
                     panelActions={panelActions}
                     variables={variables}
                     dispatch={this.props.dispatch}/>
      <RecommendedWrfDialog open={this.state.wrfDialogOpen}
                              onClose={this.closeWrfDialog}
                              data_set={data_set}
                              variables={variables}
                              dispatch={this.props.dispatch}/>
    </div>;
  }
}

const mapStateToProps = state => {
  const data_set = state.graphs.data_set;
  const sliceKey = SLICE_BY_DATA_SET[data_set];
  const s = state[sliceKey];
  return {
    data_set,
    variables: {
      slope: s.slope,
      surface_hazard: s.surface_hazard,
      near_surface_hazard: s.near_surface_hazard,
      near_surface_height: s.near_surface_height,
      elevated_hazard: s.elevated_hazard,
      elevated_height: s.elevated_height,
      bark_hazard: s.bark_hazard,
      wind_reduction_factor: s.wind_reduction_factor,
      calculated_fuel_load: s.calculated_fuel_load,
      overstorey_cover: s.overstorey_cover,
      overstorey_height: s.overstorey_height,
      tall_understorey_cover: s.tall_understorey_cover,
      aspect: s.aspect,
      slope_steepness: s.slope_steepness,
      lag_temp_rh: s.lag_temp_rh,
      summer_afternoon_fmc: s.summer_afternoon_fmc
    }
  };
};

const mapDispatchToProps = dispatch => ({ dispatch });

export default connect(mapStateToProps, mapDispatchToProps)(VestaMk2Variables);



// WEBPACK FOOTER //
// ./src/containers/VestaMk2Variables.js